// src/components/Rezervace.tsx
import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { cs, enGB, de } from 'date-fns/locale';
import Obsazenost from './Obsazenost';
import { useTranslation } from 'react-i18next';
import { formatApproxEur, formatPrice } from '../utils/currency';
import { useReservation } from '../hooks/useReservation';
import { usePricing } from '../hooks/usePricing';

const EXTRA_BED_PRICE = 350;

const Rezervace: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const datepickerLocale = React.useMemo(() => {
    const lng = (i18n.language || 'cs').slice(0, 2);
    if (lng === 'de') return de;
    if (lng === 'cs') return cs;
    return enGB;
  }, [i18n.language]);

  const {
    setDates,
    blockedDates,
    arrivalTime,
    setArrivalTime,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    countryCode,
    setCountryCode,
    phone,
    setPhone,
    adults,
    setAdults,
    children,
    setChildren,
    needsCrib,
    setNeedsCrib,
    needsExtraBed,
    setNeedsExtraBed,
    paymentMethod,
    setPaymentMethod,
    note,
    setNote,
    error,
    isSubmitting,
    isLoading,
    submitReservation,
    startDate,
    endDate,
    totalPeople,
    MAX_PEOPLE,
    formatLocalDate
  } = useReservation();

  const {
    totalPrice,
    lateArrivalFee,
    nights,
    season
  } = usePricing({
    startDate,
    endDate,
    adults,
    children,
    needsExtraBed,
    arrivalTime
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const reservation = await submitReservation(totalPrice, nights, season);
    if (reservation) {
      const reservationWithFlags = {
        ...reservation,
        paymentMethod: paymentMethod ?? reservation.paymentMethod,
        isVerified: reservation.isVerified ?? false
      };
      localStorage.setItem('reservationData', JSON.stringify(reservationWithFlags));
      void navigate('/verify', { state: { reservation: reservationWithFlags, paymentMethod } });
    }
  };

  const LoadingCard = (
    <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
      <p className="text-lg text-gray-600">{t('reservation.loadingCalendar')}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f7f2ed] text-[#3f2b25]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        {isLoading ? LoadingCard : (
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">{t('reservation.title')}</h2>
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
              {/* Datum příjezdu a odjezdu */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('reservation.dateRange')}</label>
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update: [Date | null, Date | null]) => setDates(update)}
                  minDate={new Date()}
                  excludeDates={Array.from(blockedDates).map(d => {
                    const [year, month, day] = d.split('-').map(Number);
                    return new Date(year, month - 1, day);
                  })}
                  dateFormat="dd.MM.yyyy"
                  className="w-full border rounded p-2 focus:ring-2 focus:ring-[#4B2E2E] focus:border-[#4B2E2E]"
                  placeholderText={t('datepicker.placeholder')}
                  required
                  locale={datepickerLocale}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  dayClassName={date =>
                    blockedDates.has(formatLocalDate(date)) ? 'react-datepicker__day--excluded' : ''
                  }
                />
              </div>

              {/* Čas příjezdu */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('reservation.arrivalTime')}</label>
                <select
                  value={arrivalTime}
                  onChange={e => setArrivalTime(e.target.value)}
                  className="w-full border rounded p-2 focus:ring-2 focus:ring-[#4B2E2E] focus:border-[#4B2E2E]"
                >
                  {Array.from({ length: 8 }, (_, i) => {
                    const hour = 16 + i;
                    return (
                      <option key={hour} value={`${hour}:00`}>
                        {`${hour}:00`}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Jméno a příjmení */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('reservation.firstName')}</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    className="w-full border rounded p-2 focus:ring-2 focus:ring-[#4B2E2E] focus:border-[#4B2E2E]"
                    placeholder={t('reservation.firstName')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('reservation.lastName')}</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    className="w-full border rounded p-2 focus:ring-2 focus:ring-[#4B2E2E] focus:border-[#4B2E2E]"
                    placeholder={t('reservation.lastName')}
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('reservation.email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full border rounded p-2 focus:ring-2 focus:ring-[#4B2E2E] focus:border-[#4B2E2E]"
                  placeholder="email@example.com"
                  required
                />
              </div>

              {/* Telefon */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('reservation.phone')}</label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={e => setCountryCode(e.target.value)}
                    className="border rounded p-2"
                  >
                    <option value="+420">🇨🇿 +420</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+43">🇦🇹 +43</option>
                    <option value="+41">🇨🇭 +41</option>
                    <option value="+421">🇸🇰 +421</option>
                    <option value="+48">🇵🇱 +48</option>
                  </select>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    className="flex-1 border rounded p-2 focus:ring-2 focus:ring-[#4B2E2E] focus:border-[#4B2E2E]"
                    placeholder="123456789"
                    pattern="\d{9,12}"
                    required
                  />
                </div>
              </div>

              {/* Počet osob */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('reservation.adults')}</label>
                  <select
                    value={adults}
                    onChange={e => setAdults(Number(e.target.value))}
                    className="w-full border rounded p-2"
                  >
                    {Array.from({ length: Math.min(MAX_PEOPLE - children, MAX_PEOPLE) }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('reservation.children')}</label>
                  <select
                    value={children}
                    onChange={e => setChildren(Number(e.target.value))}
                    className="w-full border rounded p-2"
                  >
                    {Array.from({ length: MAX_PEOPLE - adults + 1 }, (_, i) => i).map(n => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Doplňky */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={needsCrib}
                    onChange={e => setNeedsCrib(e.target.checked)}
                    className="mr-2 h-4 w-4 text-[#4B2E2E] border-gray-300 rounded"
                  />
                  <span>{t('reservation.crib')}</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={needsExtraBed}
                    onChange={e => setNeedsExtraBed(e.target.checked)}
                    disabled={totalPeople !== 8}
                    className="mr-2 h-4 w-4 text-[#4B2E2E] border-gray-300 rounded"
                  />
                  <span className={totalPeople !== 8 ? 'text-gray-400' : ''}>
                    {t('reservation.extraBed')} (+{formatPrice(EXTRA_BED_PRICE, i18n.language)}/{t('pricing.nights')})
                  </span>
                </label>
              </div>

              {/* Platba */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('reservation.paymentMethod')}</label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="transfer"
                      checked={paymentMethod === 'transfer'}
                      onChange={() => setPaymentMethod('transfer')}
                      className="mt-1"
                    />
                    <div className="flex items-center gap-3">
                      {/* Ikona převodu (stylizovaná karta) */}
                      <div className="flex-shrink-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <rect x="2" y="6" width="20" height="12" rx="2" stroke="#4B2E2E" strokeWidth="2" />
                          <circle cx="8" cy="12" r="1.5" fill="#4B2E2E" />
                          <circle cx="16" cy="12" r="1.5" fill="#4B2E2E" />
                          <path d="M2 10h20" stroke="#4B2E2E" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold">{t('reservation.payment.transfer')}</div>
                        {paymentMethod === 'transfer' && (
                          <div className="text-sm text-gray-600">{t('reservation.payment.transfer.details')}</div>
                        )}
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={paymentMethod === 'cash'}
                      onChange={() => setPaymentMethod('cash')}
                      className="mt-1"
                    />
                    <div className="flex items-center gap-3">
                      {/* Ikona hotovosti */}
                      <div className="flex-shrink-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <rect x="3" y="8" width="18" height="8" rx="2" stroke="#4B2E2E" strokeWidth="2" />
                          <circle cx="12" cy="12" r="3" stroke="#4B2E2E" strokeWidth="2" />
                          <path d="M6 8V6h12v2" stroke="#4B2E2E" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold">{t('reservation.payment.cash')}</div>
                        {paymentMethod === 'cash' && (
                          <div className="text-sm text-gray-600">{t('reservation.payment.cash.details')}</div>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Poznámka */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('reservation.note')}</label>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  rows={4}
                  className="w-full border rounded p-2 focus:ring-2 focus:ring-[#4B2E2E] focus:border-[#4B2E2E]"
                  placeholder={t('reservation.note')}
                />
              </div>

              {/* Cena */}
              {startDate && endDate && totalPeople >= 1 && (
                <div className="bg-gray-100 p-4 rounded text-gray-800 space-y-1">
                  <div>
                    <strong>{t('reservation.nights')}:</strong> {nights}
                  </div>
                  <div>
                    <strong>{t('reservation.totalPrice')}:</strong> {totalPrice.toLocaleString()} Kč
                    {i18n.language !== 'cs' && (
                      <div className="text-sm text-gray-600 mt-1">(~ {formatApproxEur(totalPrice, i18n.language)})</div>
                    )}
                  </div>
                  {lateArrivalFee > 0 && (
                    <div>
                      <strong>{t('reservation.lateArrivalFee')}:</strong> {formatPrice(lateArrivalFee, i18n.language)}
                    </div>
                  )}
                </div>
              )}

              {/* Chyby */}
              {error && <div className="text-red-600 font-medium">{error}</div>}

              {/* Odeslat */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#4B2E2E] text-white py-3 rounded-lg font-semibold hover:brightness-110 transition disabled:opacity-60"
              >
                {isSubmitting ? t('reservation.submitting') : t('reservation.submit')}
              </button>
            </form>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-6">
          <Obsazenost />
        </div>
      </div>
    </div>
  );
};

export default Rezervace;
